<template>
  <div class="layout-column-header">
    <div class="primary column-handle p-2 select-none sm:rounded-md" v-bind="$attrs">
      <div v-if="showTitle" class="hidden items-center justify-between py-0.5 sm:flex">
        <div class="font-heading text-sm select-none">{{ columnName }}</div>
        <div :style="closeButtonWrapperStyle">
          <GeneralButton
            v-if="isLayoutColumnClosable"
            color="primary"
            name="close-column"
            class="hover:text-warning p-0!"
            :icon="['fas', 'xmark']"
            :disabled="isCloseDisabled"
            title="Kolom sluiten"
            @click="closeColumn"
          />
        </div>
      </div>
      <div class="flex min-h-8 items-start justify-between space-x-2 sm:space-x-0">
        <div class="flex w-full justify-between space-x-2">
          <div class="grow">
            <slot />
          </div>
          <div v-if="hasSearch && showSearchButton">
            <GeneralButton
              :color="searchExpanded ? 'secondary' : 'surface'"
              title="Zoeken"
              @click="toggleSearch"
              :icon="['fas', 'magnifying-glass']"
              :class="{ relative: filterCount > 0 }"
            >
              <ActiveIndicatorBadge v-if="filterCount > 0" :toggle="!searchExpanded">
                {{ filterCount }}
              </ActiveIndicatorBadge>
            </GeneralButton>
          </div>
        </div>
        <NavigationMenu v-if="showNavigationMenu" class="sm:hidden" />
      </div>
    </div>
    <slot v-if="hasSearch && searchExpanded" name="search">
      <SearchBar v-model="defaultSearchText" :placeholder="searchPlaceholder" />
    </slot>
    <div
      v-if="$slots.filters"
      v-show="searchExpanded"
      class="gray-lighten-2 space-y-2 rounded-md p-2"
    >
      <slot name="filters" />
      <GeneralButton
        variant="outlined"
        size="small"
        v-if="$slots['filters-expanded']"
        @click="toggleFiltersExpanded"
        :icon="['fas', filtersExpanded ? 'chevron-up' : 'chevron-down']"
      >
        Meer filters
      </GeneralButton>
      <div v-if="$slots['filters-expanded']" v-show="filtersExpanded">
        <slot name="filters-expanded" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { inject, computed, type ComputedRef, ref } from "vue"
import GeneralButton from "@/components/inputs/GeneralButton.vue"
import ActiveIndicatorBadge from "@/components/misc/ActiveIndicatorBadge.vue"
import SearchBar from "@/components/misc/SearchBar.vue"
import type { LayoutColumn } from "@/models/user_settings/layout-column.model"
import { useLayoutColumnsStore } from "@/store/layout-columns.store"
import NavigationMenu from "@/components/navigation/NavigationMenu.vue"
import type { LayoutController } from "@/controllers/layout-controller.ts"
import { LayoutColumnComponents } from "@/enums/models/layout-column-components.enum"

// Inject the layout column from the parent LayoutColumn component
const layoutColumn = inject<ComputedRef<LayoutColumn> | null>("layoutColumn", null)

const isLayoutColumnClosable = inject("isLayoutColumnClosable", false)

const layoutColumnsStore = useLayoutColumnsStore()

const emit = defineEmits(["search-collapsed"])

const slots = defineSlots<{
  default?(): unknown
  search?(): unknown
  filters?(): unknown
  "filters-expanded"?(): unknown
}>()

const searchText = defineModel<string>("searchText")

defineOptions({
  inheritAttrs: false
})

const props = withDefaults(
  defineProps<{
    showTitle?: boolean
    showSearchButton?: boolean
    showNavigationMenu?: boolean
    filterCount?: number
    searchPlaceholder?: string
    columnTitle?: string
  }>(),
  {
    showTitle: true,
    showSearchButton: true,
    showNavigationMenu: true,
    filterCount: 0,
    searchPlaceholder: "Zoeken..."
  }
)

const defaultSearchText = computed({
  get: () => searchText.value ?? "",
  set: (value: string) => {
    searchText.value = value
  }
})

const hasSearch = computed(() => Boolean(slots.search) || searchText.value !== undefined)

const columnName = computed(() => {
  return props.columnTitle ?? layoutColumn?.value?.getName() ?? ""
})

const isLastColumn = computed(() => {
  if (!layoutColumn?.value?.id || !layoutColumnsStore.layoutColumns.length) {
    return false
  }
  const currentIndex = layoutColumnsStore.layoutColumns.findIndex(
    (column) => column.id === layoutColumn.value.id
  )
  return currentIndex === layoutColumnsStore.layoutColumns.length - 1
})

const closeButtonWrapperStyle = computed(() => ({
  marginRight: isLastColumn.value
    ? "max(4rem, var(--layout-column-header-right-offset, 0px))"
    : "var(--layout-column-header-right-offset, 0px)"
}))

const isCloseDisabled = computed(() => {
  const columns = layoutColumnsStore.layoutColumns
  // Only allow closing the layout controls column if it is the only column.
  return (
    columns.length === 1 &&
    columns[0].component === LayoutColumnComponents.LAYOUT_CONTROLS &&
    layoutColumn?.value?.component === LayoutColumnComponents.LAYOUT_CONTROLS
  )
})

const layoutController: LayoutController = inject("layoutController")!
function closeColumn() {
  if (isCloseDisabled.value) {
    return
  }

  if (layoutColumn?.value?.id) {
    layoutController.removeColumn(layoutColumn.value.id)
  }
}

const searchExpanded = ref(false)

function toggleSearch() {
  searchExpanded.value = !searchExpanded.value
  if (!searchExpanded.value) {
    emit("search-collapsed")
  }
}

const filtersExpanded = ref(false)

function toggleFiltersExpanded() {
  filtersExpanded.value = !filtersExpanded.value
}
</script>

<style lang="scss" scoped></style>
